import { categorize } from '@polaris/core/evm'
import { ChainType } from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import { UserRejectedRequestError } from '@polaris/core/utils'
import type { ConnectErrorType } from '@wagmi/core'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Writable } from 'utility-types'
import {
  useAccount,
  useConnect,
  useConnectors,
  useDisconnect,
  type Connector as WagmiConnector,
} from 'wagmi'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import type {
  EcosystemWalletOptions,
  WalletOption,
  WalletOptionsProps,
} from '@/src/hooks/wallet/use-wallet-options'

export const useEvmWalletOptions = ({
  searchWalletQuery,
  reportConnectingWalletInfo,
}: WalletOptionsProps = {}) => {
  const { ampli } = useAmplitudeAnalytics()
  const { connector, status, isConnecting } = useAccount()
  const { connectAsync: connectAsyncEvm } = useConnect()
  const { disconnect, disconnectAsync } = useDisconnect()
  const connectors = useConnectors()
  const featureFlags = useAtomValue(featureFlagsAtom)

  const [finalStatus, setFinalStatus] = useState<
    null | 'connected' | 'disconnected' | 'connecting' | 'reconnecting'
  >(null)

  // Add timeout effect to resolve to disconnected after 2 seconds
  useEffect(() => {
    if (finalStatus === null) {
      const timeoutId = setTimeout(() => {
        setFinalStatus('disconnected')
      }, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [finalStatus])

  useEffect(() => {
    // Strangely, wagmi gives no indicator that the account is reconnecting or actually disconnected on initial page load.
    // After a few renders, reconnection (or determining disconnection)
    // actually occurs (when isConnecting=true whether a wallet is connected or disconnected).
    // So we wait for that to determine actual readiness.
    if (isConnecting || status === 'reconnecting') {
      setFinalStatus('connecting')
    } else if (finalStatus === 'connecting') {
      setFinalStatus(status)
    }
  }, [isConnecting, finalStatus, status])

  // Effect to disconnect OKX wallet if connected
  // OKX is known to have issues executing routes, see: https://linear.app/osmosis/issue/POLFE-285/filter-okx-from-cosmosusewalletoptions-and-remove-existing-connections
  useEffect(() => {
    if (connector?.name.includes('OKX')) {
      disconnect()
    }
  }, [connector, disconnect])

  const evmWallets = useMemo(
    () =>
      filterEvmConnectors({
        searchWalletQuery,
        connectors,
      }),
    [connectors, searchWalletQuery],
  )

  const connect = useCallback(
    async ({
      wallet,
      onRetry,
      logEvent = true,
    }: {
      wallet: WalletOption
      onRetry?: () => Promise<void>
      logEvent?: boolean
    }) => {
      await disconnectAsync()

      const newConnector = evmWallets.find((c) => c.id === wallet.id)

      if (!newConnector) {
        throw new Error('Connector not found')
      }

      const baseInfo = {
        icon: newConnector.icon ?? '',
        name: newConnector.name,
        type: ChainType.Evm,
      }
      try {
        reportConnectingWalletInfo?.({
          ...baseInfo,
          status: 'connecting',
        })
        await connectAsyncEvm({
          connector: newConnector,
        }).catch(categorize)

        if (logEvent && ampli) {
          ampli.walletConnectWalletCompleted({
            wallet: newConnector.name,
            type: 'evm',
          })
        }
      } catch (e) {
        const error = e as ConnectErrorType | { code: number }

        if (
          'name' in error &&
          error.name === 'ConnectorAlreadyConnectedError'
        ) {
          return
        }

        reportConnectingWalletInfo?.({
          ...baseInfo,
          status: 'error',
          error: {
            type: (() => {
              if (error instanceof UserRejectedRequestError) {
                return 'rejected'
              }

              if ('code' in error && error.code === -32002) {
                return 'connection-already-in-progress'
              }

              return 'unexpected'
            })(),
            onRetry: () => {
              reportConnectingWalletInfo?.({
                ...baseInfo,
                status: 'connecting',
              })
              if (onRetry) {
                return onRetry()
              }

              return connect({ wallet })
            },
          },
        })

        throw error
      }
    },
    [
      ampli,
      evmWallets,
      connectAsyncEvm,
      reportConnectingWalletInfo,
      disconnectAsync,
    ],
  )

  const connectedWalletOption = useMemo(() => {
    if (!connector) return undefined
    return {
      ...makeWalletOption(connector),
      disconnect,
    }
  }, [connector, disconnect])

  const connectableOptions = useMemo<
    EcosystemWalletOptions['connectableOptions']
  >(
    () =>
      evmWallets
        .filter((wallet) => !wallet.name.includes('OKX'))
        .map((wallet) => {
          const walletOption = makeWalletOption(wallet)
          return {
            ...walletOption,
            connect: (params) => connect({ wallet: walletOption, ...params }),
          }
        }),
    [evmWallets, connect],
  )

  return useMemo<EcosystemWalletOptions>(
    () => ({
      chainType: ChainType.Evm,
      ecosystemName: 'EVM',
      ecosystemImgUrl: '/assets/evm-wallets.svg',
      connectableOptions,
      connectedWalletOption,
      enabled: featureFlags.evmEcosystem,
      ready:
        finalStatus === 'connecting' ||
        finalStatus === 'connected' ||
        finalStatus === 'disconnected',
    }),
    [
      connectableOptions,
      connectedWalletOption,
      featureFlags.evmEcosystem,
      finalStatus,
    ],
  )
}

const filterEvmConnectors = ({
  searchWalletQuery,
  connectors,
}: {
  searchWalletQuery?: string
  connectors: ReadonlyArray<WagmiConnector>
}) =>
  connectors
    .filter(
      (connector) =>
        !!connector.icon &&
        !connector.name.includes('OKX') &&
        (!searchWalletQuery ||
          connector.name
            .toLowerCase()
            .includes(searchWalletQuery.toLowerCase())),
    )
    .map((connector) => {
      const newConnector = connector as Writable<WagmiConnector>

      const nameParts = newConnector.name.split(' ')
      // Use the same name as quirks to avoid displaying `X` wallet twice
      if (nameParts.length === 2) {
        newConnector.name = nameParts[0]
      }

      return newConnector
    })

function makeWalletOption(wallet: WagmiConnector): WalletOption {
  return {
    id: wallet.id,
    displayName: wallet.name.replace(/[^\w\s-]/g, ''),
    // Some wallets return logos with leading/trailing whitespace, need to trim
    // This will cause Next.js Image component to throw downstream
    logoUrl: (wallet.icon ?? '').trim(),
    onlySupportsChains: SupportedChains[wallet.name],
  }
}

const SupportedChains: Partial<Record<string, string[]>> = {
  Phantom: ['evm:1', 'evm:8453', 'evm:137'], // mainnet, base, polygon
}
